import { VibratoClient } from "@/lib/vibrato-api"
import { Button, Modal, ModalBody, ModalHeader } from "flowbite-react"
import {
  Clock,
  Database,
  Heart,
  MicrophoneSpeaking,
  NavArrowRight,
  PhonePlus,
  ShieldCheck,
  VoiceCircle
} from "iconoir-react"
import React, { useContext, useEffect, useState } from "react"
import { AppleInAppPurchasesContext, UserContext, VibratoClientContext } from "./contexts"
import { settings } from "@/settings"
import { LoadingSpinner } from "../LoadingSpinner"

export default function PricingTableModal({
  title = "Upgrade now",
  showModal,
  setShowModal,
  initialView = "individual",
  header = "Transform Your Communication with AI",
  details = "Join thousands of satisfied users who have revolutionized their calling experience with Vibrato. Choose from our tailored plans designed to meet your unique needs, powered by cutting-edge AI technology.",
  errorMsg,
  onClose
}: {
  title: string
  showModal: boolean
  setShowModal: (show: boolean) => void
  initialView: string
  header: string
  details: string
  errorMsg?: string | React.ReactNode
  onClose?: () => void
}): React.JSX.Element {
  return (
    <>
      <Modal
        show={showModal}
        onClose={() => {
          if (onClose) {
            onClose()
          }
          setShowModal(false)
        }}
        size={settings.SHOW_NATIVE_APP ? "md" : "7xl"}
        className="pt-safe"
      >
        <ModalHeader className="border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src="/vibrato-logo-alt.svg" alt="Vibrato Logo" className="h-8 w-auto" />
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500 ml-2">
              {settings.SHOW_NATIVE_APP ? "" : title}
            </h2>
          </div>
        </ModalHeader>
        <ModalBody>
          {settings.SHOW_NATIVE_APP ? (
            <>
              {settings.IS_ANDROID ? (
                <CompactMobilePricing errorMsg={errorMsg} header={header} details={details} />
              ) : (
                <IOSPricingTable errorMsg={errorMsg} header={header} details={details} />
              )}
            </>
          ) : (
            <PricingTable errorMsg={errorMsg} header={header} details={details} initialView={initialView} />
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

// @ts-ignore
function IOSPricingTable({
  errorMsg,
  header,
  details
}: {
  errorMsg?: string | React.ReactNode
  header?: string
  details?: string
}): React.JSX.Element {
  const appleInAppPurchases = useContext(AppleInAppPurchasesContext)
  const user = useContext(UserContext)
  const [showCouponModal, setShowCouponModal] = useState(false)

  if (!appleInAppPurchases) {
    return <div>No Apple In App Purchases</div>
  }

  const { plusProduct, purchasePlus, loading, processingTransaction, transactionError } = appleInAppPurchases

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner className="w-8 h-8 text-purple-600 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Loading pricing options...</p>
      </div>
    )
  }

  if (!plusProduct || !purchasePlus) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="text-red-600 text-center">
          <p className="font-medium mb-2">Unable to load pricing options</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please try again later or contact support if the issue persists.
          </p>
        </div>
      </div>
    )
  }

  const handlePurchasePlus = async () => {
    if (processingTransaction || !purchasePlus) return

    try {
      await purchasePlus()
    } catch (error) {
      console.error("Purchase failed:", error)
    }
  }

  // const handleViewMorePlans = () => {
  //   window.open("https://app.getvibrato.com/dashboard?show_pricing=true", "_blank")
  // }

  const isCurrentPlan = user.plan === "plus"
  const plusPrice = plusProduct?.pricing?.price

  if (!plusPrice) {
    console.error("No price found for plus product", plusProduct)
  }

  return (
    <>
      <CouponCodeModal showModal={showCouponModal} setShowModal={setShowCouponModal} />

      <div className="px-0 py-0">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500 mb-2">
            {header || "Upgrade to Vibrato Plus"}
          </h2>
          {errorMsg &&
            (typeof errorMsg === "string" ? (
              <p className="text-red-600 text-sm mb-4">{errorMsg}</p>
            ) : (
              <div className="text-red-600 text-sm mb-4">{errorMsg}</div>
            ))}

          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            {details || "Unlock unlimited AI calling with our most popular plan. Perfect for power users and creators."}
          </p>

          {/* Coupon Code Link */}
          <div className="mt-4">
            <button
              onClick={() => setShowCouponModal(true)}
              className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium underline decoration-dotted underline-offset-4 transition-colors duration-200"
            >
              Have a coupon code?
            </button>
          </div>
        </div>

        {/* Plus Plan Card */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-green-500 shadow-lg mb-6">
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
              Most Popular
            </span>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-xl text-gray-900 dark:text-white">Vibrato Plus</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unlimited AI calling</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{plusPrice}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">/month</div>
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2 justify-center">
            <div className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600">
              Unlimited minutes
            </div>
            <div className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 opacity-90">
              Voice cloning
            </div>
          </div>

          {/* Features Grid */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Unlimited AI calls for personal use</span>
            </div>
            <div className="flex items-center gap-3">
              <VoiceCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Clone your voice for authentic calls</span>
            </div>
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Store up to 50 contact profiles</span>
            </div>
            <div className="flex items-center gap-3">
              <MicrophoneSpeaking className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Save up to 10 conversation templates</span>
            </div>
            <div className="flex items-center gap-3">
              <PhonePlus className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">API access for integrations</span>
            </div>
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Priority customer support</span>
            </div>
          </div>

          {transactionError && (
            <div className="text-red-600 text-sm mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="font-medium">{transactionError}</p>
            </div>
          )}

          {/* CTA Button */}
          {isCurrentPlan ? (
            <button
              disabled
              className="w-full py-3 px-4 text-sm font-semibold text-gray-500 bg-gray-200 dark:bg-gray-700 dark:text-gray-400 rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
            >
              Current Plan
            </button>
          ) : (
            <button
              onClick={handlePurchasePlus}
              disabled={processingTransaction}
              className="w-full py-4 px-4 text-base font-semibold text-white rounded-lg transition-all duration-200 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingTransaction ? (
                <>
                  <LoadingSpinner className="w-4 h-4 text-white" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade to Plus
                  <NavArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Alternative Plans CTA */}
        {/* <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <div className="text-center">
          <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Need something different?</h4>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            We offer pay-as-you-go and budget-friendly plans starting at just $4.99/month. Compare all options and find
            the perfect fit for your needs.
          </p>
          <button
            onClick={handleViewMorePlans}
            className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center justify-center gap-1 mx-auto"
          >
            View all plans on website
            <NavArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div> */}

        {/* Guarantee */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <span className="font-medium text-green-800 dark:text-green-300 text-sm">30-Day Money Back Guarantee</span>
          </div>
          <p className="text-xs text-green-700 dark:text-green-400 leading-relaxed">
            Try Vibrato Plus risk-free. If you're not completely satisfied, we'll refund your purchase within 30 days,
            no questions asked.
          </p>
        </div>

        {/* Required subscription information for Apple App Store compliance */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6 px-2">
          <p className="leading-relaxed">
            Payment will be charged to your Apple account. The subscription automatically renews unless canceled at
            least 24 hours before the end of the current period. You can manage your subscription in your App Store
            account settings.
          </p>
          <p className="mt-4">
            <a
              href="https://www.getvibrato.com/c/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Use
            </a>
            <span className="mx-2">&middot;</span>
            <a
              href="https://www.getvibrato.com/c/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </>
  )
}

function CompactMobilePricing({
  errorMsg,
  header,
  details
}: {
  errorMsg?: string | React.ReactNode
  header?: string
  details?: string
}): React.JSX.Element {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const [checkoutUrls, setCheckoutUrls] = useState<any>(null)
  const [stripePortalUrl, setStripePortalUrl] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [loadingCheckoutUrls, setLoadingCheckoutUrls] = useState(true)
  const [showCouponModal, setShowCouponModal] = useState(false)

  useEffect(() => {
    setLoadingCheckoutUrls(true)
    vibratoClient
      .getUserSubscriptionCheckoutDetails()
      .then((details: any) => {
        setCheckoutUrls(details)
      })
      .catch((err: any) => {
        console.error("Error fetching subscription checkout url", err)
      })
      .finally(() => {
        setLoadingCheckoutUrls(false)
      })

    vibratoClient
      .getUserStripeDetails()
      .then((details: any) => {
        setStripePortalUrl(details.stripe_portal_url)
      })
      .catch((err: any) => {
        console.error("Error fetching user stripe details", err)
      })
  }, [])

  const handleGetStarted = (plan: string) => {
    if (loadingCheckoutUrls) return

    if (user.plan && user.plan !== "freemium") {
      // User is already subscribed, show confirmation modal for plan switch
      setSelectedPlan(plan)
      setShowConfirmModal(true)
    } else {
      // User is not subscribed, go to checkout
      settings.ENABLE_RELOAD_ON_NEW_PLAN = false
      const checkoutUrl = checkoutUrls?.[plan]?.checkout_url
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        // Fallback to website with pricing parameter
        window.open(`https://app.getvibrato.com/dashboard?show_pricing=true&plan=${plan}`, "_blank")
      }
    }
  }

  const handleManageSubscription = () => {
    if (stripePortalUrl) {
      window.location.href = stripePortalUrl
    }
  }

  const switchPlan = async () => {
    if (!selectedPlan) {
      console.error("No plan selected")
      return
    }

    settings.ENABLE_RELOAD_ON_NEW_PLAN = false
    await vibratoClient.switchStripePlan(selectedPlan)

    console.log("Successfully switched plan")

    for (let i = 0; i < 30; i++) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const user = await vibratoClient.getUserApiSession()
      if (user.plan === selectedPlan) {
        break
      }
    }

    const redirectUrl = `${window.location.origin}/new-subscription`
    console.log("Redirecting to", redirectUrl)

    window.location.href = redirectUrl
  }

  const getButtonType = (plan: string) => {
    if (user.plan === "freemium" && plan === "freemium") {
      return "showCurrent"
    }
    if (user.plan !== "freemium" && plan === user.plan) {
      return "showManage"
    }
    if (user.plan !== "freemium" && plan === "freemium") {
      return "showNothing"
    }
    return "showGet"
  }

  const plans = [
    {
      id: "plus",
      name: "Plus",
      price: "$19.99",
      tagline: "Unlimited calling",
      highlights: ["Unlimited minutes and calls"],
      features: ["Everything in Standard", "API access", "Priority support", "50 contacts"],
      color: "from-green-500 to-green-600",
      borderColor: "border-green-200",
      popular: true
    },
    {
      id: "standard",
      name: "Standard",
      price: "$9.99",
      tagline: "More minutes and features",
      highlights: ["50 min/month", "Clone your voice"],
      features: ["Everything in Lite", "$0.20/min after", "Voice options", "35 contacts"],
      color: "from-blue-500 to-blue-600",
      borderColor: "border-blue-200"
    },
    {
      id: "lite",
      name: "Lite",
      price: "$4.99",
      tagline: "Try AI calling",
      highlights: ["20 min/month", "1 phone number"],
      features: ["$0.25/min after", "Personal use", "1 phone number", "20 contacts"],
      color: "from-purple-500 to-purple-600",
      borderColor: "border-purple-200"
    }
  ]

  return (
    <>
      <ConfirmPlanSwitchModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          await switchPlan()
          setShowConfirmModal(false)
        }}
      />

      <NonAppleCouponCodeModal showModal={showCouponModal} setShowModal={setShowCouponModal} />

      <div className="px-0 py-0">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500 mb-2">
            {header || "AI Calling Plans"}
          </h2>
          {errorMsg &&
            (typeof errorMsg === "string" ? (
              <p className="text-red-600 text-sm mb-4">{errorMsg}</p>
            ) : (
              <div className="text-red-600 text-sm mb-4">{errorMsg}</div>
            ))}
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            {details || "Choose the plan that fits your calling needs. Upgrade or cancel anytime."}
          </p>
          {/* Coupon Code Link */}
          <div className="mt-4">
            <button
              onClick={() => setShowCouponModal(true)}
              className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium underline decoration-dotted underline-offset-4 transition-colors duration-200"
            >
              Have a coupon code?
            </button>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {plans.map((plan) => {
            const buttonType = getButtonType(plan.id)
            return (
              <div
                key={plan.id}
                className={`relative bg-white dark:bg-gray-800 rounded-xl p-4 border-2 transition-all duration-200 ${
                  plan.popular ? "border-blue-500 shadow-lg scale-[1.02]" : `${plan.borderColor} hover:shadow-md`
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">Vibrato {plan.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{plan.tagline}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{plan.price}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">/month</div>
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap gap-2 justify-center">
                  {plan.highlights.map((highlight, index) => (
                    <div
                      key={index}
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium text-white bg-gradient-to-r ${plan.color} ${index > 0 ? "ml-2 opacity-90" : ""}`}
                    >
                      {highlight}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="text-xs text-gray-600 dark:text-gray-300 text-center">
                      {feature}
                    </div>
                  ))}
                </div>

                {buttonType === "showCurrent" && (
                  <button
                    disabled
                    className="w-full py-3 px-4 text-sm font-semibold text-gray-500 bg-gray-200 dark:bg-gray-700 dark:text-gray-400 rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    Current Plan
                  </button>
                )}

                {buttonType === "showGet" && (
                  <button
                    onClick={() => handleGetStarted(plan.id)}
                    className={`w-full py-3 px-4 text-sm font-semibold text-white rounded-lg transition-all duration-200 bg-gradient-to-r ${plan.color} hover:shadow-lg active:scale-95 flex items-center justify-center gap-2`}
                  >
                    {loadingCheckoutUrls ? (
                      <>
                        <LoadingSpinner className="w-4 h-4 text-white" />
                        Loading...
                      </>
                    ) : (
                      <>
                        {user.plan && user.plan !== "freemium" ? "Switch Plan" : "Get Started"}
                        <NavArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}

                {buttonType === "showManage" && (
                  <button
                    onClick={handleManageSubscription}
                    className={`w-full py-3 px-4 text-sm font-semibold text-white rounded-lg transition-all duration-200 bg-gradient-to-r ${plan.color} hover:shadow-lg active:scale-95 flex items-center justify-center gap-2`}
                  >
                    Manage Subscription
                    <NavArrowRight className="w-4 h-4" />
                  </button>
                )}

                {buttonType === "showNothing" && <div className="py-3"></div>}
              </div>
            )
          })}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <span className="font-medium text-gray-900 dark:text-white text-sm">30-Day Money Back Guarantee</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            Try any plan risk-free with our 30-day money back guarantee. Cancel anytime with no hidden fees. Your
            subscription is managed securely through our website.
          </p>
        </div>
      </div>
    </>
  )
}

interface PricingTableProps {
  initialView?: string
  header?: string
  errorMsg?: string | React.ReactNode
  details?: string
}

// Centralized plan configuration
const PLAN_CONFIG = {
  // Special display version for Mobile iOS with complete feature list
  plusMobileDisplay: {
    id: "plus",
    title: "Vibrato Plus",
    description: "Unlimited AI calling with premium features",
    price: "$19.99",
    color: "green",
    highlighted: false,
    showPerMonth: true,
    features: [
      { icon: <Clock className="w-5 h-5 text-green-600" />, text: "Unlimited AI calls for personal use" },
      { icon: <VoiceCircle className="w-5 h-5 text-green-600" />, text: "Clone and customize your own AI voice" },
      { icon: <PhonePlus className="w-5 h-5 text-green-600" />, text: "One dedicated phone number" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-green-600" />, text: "Save up to 5 conversation templates" },
      { icon: <Database className="w-5 h-5 text-green-600" />, text: "Store up to 50 contact profiles" },
      { icon: <PhonePlus className="w-5 h-5 text-green-600" />, text: "Full API access for integrations" },
      { icon: <Heart className="w-5 h-5 text-green-600" />, text: "Priority customer support" },
      { icon: <ShieldCheck className="w-5 h-5 text-green-600" />, text: "Personal use only" }
    ]
  },
  plus: {
    id: "plus",
    title: "Vibrato Plus",
    description: "Ideal for power users and creators",
    price: "$19.99",
    color: "green",
    highlighted: true,
    showPerMonth: true,
    features: [
      { icon: <Database className="w-5 h-5 text-green-600" />, text: "Everything in Standard plan" },
      { icon: <Clock className="w-5 h-5 text-green-600" />, text: "Unlimited AI calls for personal use" },
      { icon: <VoiceCircle className="w-5 h-5 text-green-600" />, text: "Create your own custom AI voice" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-green-600" />, text: "Save up to 5 conversation scripts" },
      { icon: <Database className="w-5 h-5 text-green-600" />, text: "Store up to 50 contact profiles" },
      { icon: <PhonePlus className="w-5 h-5 text-green-600" />, text: "Access to Vibrato API" },
      { icon: <Heart className="w-5 h-5 text-green-600" />, text: "Priority support" }
    ]
  },
  standard: {
    id: "standard",
    title: "Vibrato Standard",
    description: "Great for regular personal calling needs",
    price: "$9.99",
    color: "blue",
    highlighted: false,
    showPerMonth: true,
    features: [
      { icon: <Database className="w-5 h-5 text-blue-600" />, text: "Everything in Lite plan" },
      { icon: <Clock className="w-5 h-5 text-blue-600" />, text: "50 minutes of AI calls monthly" },
      { icon: <Database className="w-5 h-5 text-blue-600" />, text: "Pay-as-you-go at $0.20/min after" },
      { icon: <VoiceCircle className="w-5 h-5 text-blue-600" />, text: "Personal use only" },
      { icon: <PhonePlus className="w-5 h-5 text-blue-600" />, text: "One dedicated phone number" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-blue-600" />, text: "Save up to 4 conversation scripts" },
      { icon: <Database className="w-5 h-5 text-blue-600" />, text: "Store up to 35 contact profiles" },
      { icon: <VoiceCircle className="w-5 h-5 text-blue-600" />, text: "Clone your own voice" }
    ]
  },

  lite: {
    id: "lite",
    title: "Vibrato Lite",
    description: "Perfect for trying out AI-powered calling",
    price: "$4.99",
    color: "purple",
    highlighted: false,
    showPerMonth: true,
    features: [
      { icon: <Clock className="w-5 h-5 text-purple-600" />, text: "20 minutes of AI calls monthly" },
      { icon: <Database className="w-5 h-5 text-purple-600" />, text: "Pay-as-you-go at $0.25/min after" },
      { icon: <VoiceCircle className="w-5 h-5 text-purple-600" />, text: "Personal use only" },
      { icon: <PhonePlus className="w-5 h-5 text-purple-600" />, text: "One dedicated phone number" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-purple-600" />, text: "Save up to 3 conversation scripts" },
      { icon: <Database className="w-5 h-5 text-purple-600" />, text: "Store up to 20 contact profiles" }
    ]
  },

  pro: {
    id: "pro",
    title: "Business Pro",
    description: "Perfect for small teams and growing businesses",
    price: "$49.99",
    color: "indigo",
    highlighted: false,
    showPerMonth: true,
    features: [
      { icon: <Clock className="w-5 h-5 text-indigo-600" />, text: "700 AI call minutes monthly" },
      { icon: <Database className="w-5 h-5 text-indigo-600" />, text: "Additional minutes at $0.11/min" },
      { icon: <VoiceCircle className="w-5 h-5 text-indigo-600" />, text: "Approved for commercial use" },
      { icon: <PhonePlus className="w-5 h-5 text-indigo-600" />, text: "Run 2 calls simultaneously" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-indigo-600" />, text: "Save up to 20 conversation scripts" },
      { icon: <Database className="w-5 h-5 text-indigo-600" />, text: "Manage up to 200 contacts" },
      { icon: <Heart className="w-5 h-5 text-indigo-600" />, text: "Team collaboration features" }
    ]
  },
  enterprise: {
    id: "enterprise",
    title: "Enterprise",
    description: "Custom solutions for large organizations",
    price: "Contact us",
    color: "gray",
    highlighted: false,
    showPerMonth: false,
    features: [
      { icon: <Clock className="w-5 h-5 text-gray-600" />, text: "Custom monthly minutes" },
      { icon: <Database className="w-5 h-5 text-gray-600" />, text: "Volume-based pricing" },
      { icon: <PhonePlus className="w-5 h-5 text-gray-600" />, text: "Unlimited phone numbers" },
      { icon: <MicrophoneSpeaking className="w-5 h-5 text-gray-600" />, text: "Unlimited scripts & campaigns" },
      { icon: <VoiceCircle className="w-5 h-5 text-gray-600" />, text: "Advanced team management" },
      { icon: <Heart className="w-5 h-5 text-gray-600" />, text: "Custom integrations" },
      { icon: <Database className="w-5 h-5 text-gray-600" />, text: "Dedicated account manager" }
    ]
  }
}

export function PricingTable({
  initialView = "individual",
  header = "Transform Your Communication with AI",
  errorMsg = null,
  details = "Experience the future of communication with plans designed for everyone. Our AI-powered calling solutions help you save time, reduce costs, and communicate more effectively."
}: PricingTableProps): React.JSX.Element {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [checkoutUrls, setCheckoutUrls] = useState<any>(null)
  const [stripePortalUrl, setStripePortalUrl] = useState<any>(null)
  const [currentView, setCurrentView] = useState(initialView)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [loadingCheckoutUrls, setLoadingCheckoutUrls] = useState(true)
  const [showCouponModal, setShowCouponModal] = useState(false)

  useEffect(() => {
    console.log("settings.IS_MOBILE_SAFARI", settings.IS_MOBILE_SAFARI)
    if (settings.IS_MOBILE_SAFARI) {
      setCurrentView("individual")
    }
  }, [settings.IS_MOBILE_SAFARI])

  useEffect(() => {
    setLoadingCheckoutUrls(true)
    vibratoClient
      .getUserSubscriptionCheckoutDetails()
      .then((details: any) => {
        setCheckoutUrls(details)
      })
      .catch((err: any) => {
        console.error("Error fetching subscription checkout url", err)
      })
      .finally(() => {
        setLoadingCheckoutUrls(false)
      })

    vibratoClient
      .getUserStripeDetails()
      .then((details: any) => {
        setStripePortalUrl(details.stripe_portal_url)
      })
      .catch((err: any) => {
        console.error("Error fetching user stripe details", err)
      })
  }, [])

  const showBizPlans = true

  const switchPlan = async () => {
    if (!selectedPlan) {
      console.error("No plan selected")
      return
    }

    await vibratoClient.switchStripePlan(selectedPlan)

    console.log("Successfully switched plan")

    for (let i = 0; i < 30; i++) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const user = await vibratoClient.getUserApiSession()
      if (user.plan === selectedPlan) {
        break
      }
    }

    const redirectUrl = `${window.location.origin}/new-subscription`
    console.log("Redirecting to", redirectUrl)

    window.location.href = redirectUrl
  }

  return (
    <section className="bg-white dark:bg-gray-900">
      <ConfirmPlanSwitchModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          await switchPlan()
          setShowConfirmModal(false)
        }}
      />
      <NonAppleCouponCodeModal showModal={showCouponModal} setShowModal={setShowCouponModal} />

      <div className="flex justify-center pb-6 px-4 mx-auto max-w-(--breakpoint-xl)">
        <div className="max-w-(--breakpoint-md) text-center">
          <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500">
            {header}
          </h2>
          {errorMsg &&
            (typeof errorMsg === "string" ? (
              <p className="font-light text-red-600 sm:text-xl my-6 text-center">{errorMsg}</p>
            ) : (
              <div className="font-light text-red-600 sm:text-xl my-6 text-center">{errorMsg}</div>
            ))}
          {details && <p className="font-light text-gray-600 sm:text-xl dark:text-gray-400">{details}</p>}
          {/* Coupon Code Link */}
          <div className="mt-4">
            <button
              onClick={() => setShowCouponModal(true)}
              className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium underline decoration-dotted underline-offset-4 transition-colors duration-200"
            >
              Have a coupon code?
            </button>
          </div>
        </div>
      </div>
      {showBizPlans && !settings.IS_MOBILE_SAFARI && (
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-700 rounded-lg shadow-sm">
            <button
              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                currentView === "individual"
                  ? "bg-white dark:bg-gray-600 text-purple-600 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
              }`}
              onClick={() => setCurrentView("individual")}
            >
              Individual Plans
            </button>
            <button
              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                currentView === "business"
                  ? "bg-white dark:bg-gray-600 text-purple-600 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
              }`}
              onClick={() => setCurrentView("business")}
            >
              Business Plans
            </button>
          </div>
        </div>
      )}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 ${
          currentView === "business"
            ? "lg:grid-cols-2 lg:max-w-4xl"
            : settings.IS_MOBILE_SAFARI
              ? "lg:grid-cols-1 lg:max-w-md"
              : "lg:grid-cols-3"
        } gap-6 px-4 pb-8 mx-auto max-w-(--breakpoint-xl)`}
      >
        {currentView === "individual" && (
          <>
            {settings.IS_MOBILE_SAFARI ? (
              // Only show Plus plan on Mobile Safari with complete feature list
              <PricingCard
                key={PLAN_CONFIG.plusMobileDisplay.id}
                plan={PLAN_CONFIG.plusMobileDisplay.id}
                title={PLAN_CONFIG.plusMobileDisplay.title}
                description={PLAN_CONFIG.plusMobileDisplay.description}
                price={PLAN_CONFIG.plusMobileDisplay.price}
                link={checkoutUrls?.plus?.checkout_url}
                manageSubsLink={stripePortalUrl}
                features={PLAN_CONFIG.plusMobileDisplay.features}
                setSelectedPlan={setSelectedPlan}
                setShowConfirmModal={setShowConfirmModal}
                color={PLAN_CONFIG.plusMobileDisplay.color}
                loadingCheckoutUrls={loadingCheckoutUrls}
              />
            ) : (
              // Show all individual plans on other platforms
              <>
                {["plus", "standard", "lite"].map((planId) => {
                  const plan = PLAN_CONFIG[planId as keyof typeof PLAN_CONFIG]
                  return (
                    <PricingCard
                      key={plan.id}
                      plan={plan.id}
                      title={plan.title}
                      description={plan.description}
                      price={plan.price}
                      link={checkoutUrls?.[plan.id]?.checkout_url}
                      manageSubsLink={stripePortalUrl}
                      features={plan.features}
                      setSelectedPlan={setSelectedPlan}
                      setShowConfirmModal={setShowConfirmModal}
                      color={plan.color}
                      highlighted={plan.highlighted}
                      showPerMonth={plan.showPerMonth}
                      loadingCheckoutUrls={loadingCheckoutUrls}
                    />
                  )
                })}
              </>
            )}
          </>
        )}
        {showBizPlans && !settings.IS_MOBILE_SAFARI && currentView === "business" && (
          <>
            {["pro", "enterprise"].map((planId) => {
              const plan = PLAN_CONFIG[planId as keyof typeof PLAN_CONFIG]
              return (
                <PricingCard
                  key={plan.id}
                  plan={plan.id}
                  title={plan.title}
                  description={plan.description}
                  price={plan.price}
                  link={checkoutUrls?.[plan.id]?.checkout_url}
                  manageSubsLink={stripePortalUrl}
                  features={plan.features}
                  setSelectedPlan={setSelectedPlan}
                  setShowConfirmModal={setShowConfirmModal}
                  color={plan.color}
                  highlighted={plan.highlighted}
                  showPerMonth={plan.showPerMonth}
                  loadingCheckoutUrls={loadingCheckoutUrls}
                />
              )
            })}
          </>
        )}
      </div>
    </section>
  )
}

interface PricingCardProps {
  plan: string
  title: string
  description: string
  price: string
  features: { icon: React.ReactNode; text: string }[]
  showPerMonth?: boolean
  link?: string
  manageSubsLink?: string
  highlighted?: boolean
  color?: string
  setSelectedPlan: (plan: string) => void
  setShowConfirmModal: (show: boolean) => void
  loadingCheckoutUrls: boolean
}

function PricingCard({
  plan,
  title,
  description,
  price,
  features,
  link,
  manageSubsLink,
  showPerMonth = true,
  highlighted = false,
  color = "purple",
  setSelectedPlan,
  setShowConfirmModal,
  loadingCheckoutUrls
}: PricingCardProps) {
  const user = useContext(UserContext)

  let buttonType = "showGet"
  if (user.plan === "freemium" && plan === "freemium") {
    buttonType = "showCurrent"
  }
  if (user.plan != "freemium" && plan === user.plan) {
    buttonType = "showManage"
  }
  if (user.plan !== "freemium" && plan === "freemium") {
    buttonType = "showNothing"
  }
  if (plan === "enterprise") {
    buttonType = "showContact"
  }

  const getColorClasses = () => {
    const colorMap: { [key: string]: { bg: string; border: string; hover: string; text: string } } = {
      purple: {
        bg: "bg-purple-600",
        border: "border-purple-200",
        hover: "hover:bg-purple-700",
        text: "text-purple-600"
      },
      blue: {
        bg: "bg-blue-600",
        border: "border-blue-200",
        hover: "hover:bg-blue-700",
        text: "text-blue-600"
      },
      green: {
        bg: "bg-green-600",
        border: "border-green-200",
        hover: "hover:bg-green-700",
        text: "text-green-600"
      },
      indigo: {
        bg: "bg-indigo-600",
        border: "border-indigo-200",
        hover: "hover:bg-indigo-700",
        text: "text-indigo-600"
      },
      gray: {
        bg: "bg-gray-600",
        border: "border-gray-200",
        hover: "hover:bg-gray-700",
        text: "text-gray-600"
      }
    }
    return colorMap[color] || colorMap.purple
  }

  const colorClasses = getColorClasses()

  return (
    <div
      className={`flex flex-col p-6 text-center bg-white rounded-xl border shadow-lg transition-all duration-200 dark:bg-gray-800 ${
        highlighted ? `border-2 border-${color}-500 scale-105 z-10` : `${colorClasses.border} hover:scale-102`
      }`}
    >
      {highlighted && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className={`${colorClasses.bg} text-white text-sm font-semibold px-4 py-1 rounded-full`}>
            Most Popular
          </span>
        </div>
      )}
      <h3 className={`text-2xl font-bold ${colorClasses.text} mb-2`}>{title}</h3>
      <p className="font-light text-gray-500 dark:text-gray-400 mb-4">{description}</p>
      <div className="flex justify-center items-baseline my-4">
        <span className={`text-4xl font-extrabold ${colorClasses.text}`}>{price}</span>
        {showPerMonth && <span className="text-gray-500 dark:text-gray-400 ml-2">/month</span>}
      </div>
      <ul role="list" className="mb-8 space-y-4 text-left">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-3">
            <span className="shrink-0">{feature.icon}</span>
            <span className="text-gray-700 dark:text-gray-300">{feature.text}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto">
        {buttonType === "showCurrent" && (
          <Button size="lg" color={color} disabled className="w-full">
            Current Plan
          </Button>
        )}

        {buttonType === "showGet" && (
          <button
            className={`w-full py-3 px-6 text-base font-medium text-white rounded-lg transition-all duration-200 focus:ring-2 focus:outline-none flex items-center justify-center gap-2 ${colorClasses.bg} ${colorClasses.hover} ${loadingCheckoutUrls ? "opacity-75 cursor-not-allowed" : ""}`}
            disabled={loadingCheckoutUrls}
            onClick={() => {
              if (loadingCheckoutUrls) return

              if (user.plan && user.plan !== "freemium" && link) {
                setSelectedPlan(plan)
                setShowConfirmModal(true)
              } else if (link) {
                window.location.href = link
              }
            }}
          >
            {loadingCheckoutUrls ? (
              <>
                <LoadingSpinner className="w-5 h-5 text-white" />
                Loading...
              </>
            ) : (
              <>
                {user.plan && user.plan !== "freemium" ? "Switch Plan" : "Get Started"}
                <NavArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        )}

        {buttonType === "showManage" && (
          <button
            className={`w-full py-3 px-6 text-base font-medium text-white rounded-lg transition-all duration-200 focus:ring-2 focus:outline-none ${colorClasses.bg} ${colorClasses.hover}`}
            onClick={() => {
              if (manageSubsLink) {
                window.location.href = manageSubsLink
              }
            }}
          >
            Manage Subscription
          </button>
        )}

        {buttonType === "showContact" && (
          <button
            className={`w-full py-3 px-6 text-base font-medium text-white rounded-lg transition-all duration-200 focus:ring-2 focus:outline-none flex items-center justify-center gap-2 ${colorClasses.bg} ${colorClasses.hover}`}
            onClick={() => {
              window.open("https://calendly.com/getvibrato/vibrato-demo", "_blank")
            }}
          >
            Contact Us
            <NavArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}

function ConfirmPlanSwitchModal({
  show,
  onClose,
  onConfirm
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const [isSwitching, setIsSwitching] = useState(false)

  const handleConfirm = () => {
    setIsSwitching(true)
    onConfirm()
  }

  return (
    <Modal show={show} size="md" onClose={onClose} className="pt-safe">
      <ModalBody className="p-0">
        <div className="p-6 sm:p-8">
          {/* Header Section */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="p-4 bg-amber-100 rounded-2xl mb-4">
              <ShieldCheck className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Plan Switch</h2>
            <p className="text-gray-600 max-w-sm">
              Your current plan will be cancelled and you'll be immediately billed for the new plan.
            </p>
          </div>

          {/* Loading State */}
          {isSwitching && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent mb-4"></div>
              <p className="text-purple-600 font-medium">Switching your plan...</p>
              <p className="text-gray-500 text-sm mt-1">This may take a few moments</p>
            </div>
          )}

          {/* Action Buttons */}
          {!isSwitching && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onClose}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center min-w-[140px]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 min-w-[140px]"
              >
                Switch Plan
                <NavArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

function CouponCodeModal({
  showModal,
  setShowModal
}: {
  showModal: boolean
  setShowModal: (show: boolean) => void
}): React.JSX.Element {
  return (
    <Modal show={showModal} onClose={() => setShowModal(false)} size="md" className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500 ml-2">
            Coupon Code
          </h2>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="text-center py-6">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Use Coupon on Desktop</h3>
          </div>

          <div className="text-left bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
              To use your coupon code, you'll need to upgrade on our website using a desktop computer or laptop.
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Visit <strong className="text-purple-600 dark:text-purple-400">getvibrato.com</strong> on your desktop to
              apply your coupon and complete your upgrade.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button color="gray" onClick={() => setShowModal(false)} className="w-full">
              Close
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

function NonAppleCouponCodeModal({
  showModal,
  setShowModal
}: {
  showModal: boolean
  setShowModal: (show: boolean) => void
}): React.JSX.Element {
  return (
    <Modal show={showModal} onClose={() => setShowModal(false)} size="md" className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500 ml-2">
            Coupon Code
          </h2>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="text-center py-6">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Use Coupon at Checkout</h3>
          </div>

          <div className="text-left bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
              You can enter your coupon code on the checkout page to apply your discount.
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Continue with your plan selection and you will see an option to add a coupon on the Stripe checkout page.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button color="gray" onClick={() => setShowModal(false)} className="w-full">
              Close
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}
